// src/components/SopralluogoTab.js
import React, { useState, useEffect } from "react";
import { collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc,
  doc, serverTimestamp, getDoc, setDoc, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { prossimoProgressivo } from "../utils/contatoriUtils";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../utils/Toast";
import { confirm } from "../utils/confirm";
import { Plus, X, FileText, Printer, ChevronDown, ChevronRight, AlertTriangle, CheckSquare } from "lucide-react";
import { toLocalDateStr } from "../utils/dateUtils";
import { comprimiImmagini } from "../utils/imageUtils";
import { stampaSopralluogo } from "../utils/stampaSopralluogo";
import { SelettoreSede } from "./SediCliente";
import { copiaLibera, descrizioneSede } from "../utils/sediCliente";
import FirmaPad, { FirmaImmagine } from "./FirmaPad";
import { chiaveBozza, prefissoBozze, useBozzaModulo } from "../utils/bozzeLocali";
import AvvisoBozza, { ElencoBozze, delGiorno } from "./AvvisoBozza";
import { senzaCommessa, etichettaRC, daCollegareAllaCommessa, collegaSopralluogo, clienteDelSopralluogo, PERMESSO_SENZA_COMMESSA } from "../utils/sopralluoghi";
import { useCanSee } from "../hooks/useCanSee";

const STATI_SOP = {
  bozza:      { label: "Bozza",      bg: "#f3f4f6", col: "#6b7280" },
  completato: { label: "Completato", bg: "#d1fae5", col: "#065f46" },
  firmato:    { label: "Firmato",    bg: "#dbeafe", col: "#2563eb" },
};

// Firma canvas
// Firma: componente unico di tutta l'app (components/FirmaPad.js)
function FirmaCanvas({ onSave, onCancel, label }) {
  return <FirmaPad label={label || "Firma"} onSave={onSave} onCancel={onCancel}/>;
}

// Modal sopralluogo
// Senza projectId è un sopralluogo SENZA COMMESSA (W416): si sceglie il cliente o si scrive il
// potenziale cliente; in entrambi i casi si può legare la Richiesta Commerciale da cui nasce.
export function SopralluogoModal({ sopralluogo, projectId, projectName, codiceCommessa, clienteNome, orgId, orgInfo, utenti, onClose, onTaskCreato, commessa, riprendiBozza = false, predefiniti = null }) {
  const libero = !projectId;
  const { currentUser } = useAuth();
  const { addToast } = useToast();
  const oggi = toLocalDateStr(new Date());
  const oraOra = new Date().toTimeString().slice(0, 5);

  const [form, setForm] = useState(sopralluogo || {
    data: oggi, ora: oraOra,
    nomeCliente: clienteNome || "",
    luogo: "",
    personalePresente: [currentUser?.displayName || ""],
    altrePersone: "",
    motivoSopralluogo: "",
    situazioneRilevata: "",
    anomalie: [{ descrizione: "", gravita: "media" }],
    prescrizioni: "",
    attivitaDaEseguire: "",
    materialiNecessari: "",
    note: "",
    foto: [],
    firma: null, nomeFirmatario: "",
    stato: "bozza",
    clienteId: "", rcId: "", rcNumero: "",
    ...(predefiniti || {}),   // es. dalla Richiesta Commerciale: cliente e RC già scelti
  });
  // Richieste Commerciali da legare (della commessa, o ancora senza commessa) e clienti
  const [rcElenco, setRcElenco] = useState([]);
  const [clienti, setClienti] = useState([]);
  useEffect(() => {
    if (!orgId) return undefined;
    const u = [onSnapshot(query(collection(db, "richiesteCommerciali"), where("orgId", "==", orgId)),
      s => setRcElenco(s.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => String(b.numero || "").localeCompare(String(a.numero || "")))), () => setRcElenco([]))];
    if (libero) u.push(onSnapshot(query(collection(db, "clienti"), where("organizationId", "==", orgId)),
      s => setClienti(s.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => String(a.ragioneSociale || "").localeCompare(String(b.ragioneSociale || "")))), () => setClienti([])));
    return () => u.forEach(f => f());
  }, [orgId, libero]);
  // le RC proposte: quelle di questa commessa (o, senza commessa, quelle ancora senza commessa);
  // quella già legata resta sempre in elenco
  const rcProposte = rcElenco.filter(r => (projectId ? r.projectId === projectId : (!r.projectId || r.autonoma)) || r.id === form.rcId);
  const [showFirma, setShowFirma] = useState(false);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  // Bozza automatica sul dispositivo (utils/bozzeLocali.js)
  const bozza = useBozzaModulo({
    chiave: chiaveBozza(currentUser?.uid, "sopralluogo", projectId || "senza-commessa", sopralluogo?.id),
    valore: form,
    info: { data: form.data,
      titolo: `${sopralluogo?.numero ? `Sopralluogo ${sopralluogo.numero}` : "Nuovo sopralluogo"} — ${libero ? (clienteDelSopralluogo(form) || "senza commessa") : [codiceCommessa, projectName].filter(Boolean).join(" ")}`,
      vai: libero ? { page: "sopralluoghi-senza-commessa" } : { page: "project", projectId, initialTab: "sopralluogo" } },
    applica: dati => setForm(f => ({ ...f, ...dati })),
    riprendiSubito: riprendiBozza,
  });
  const inp = { width: "100%", padding: "6px 9px", border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 12, fontFamily: "'Inter',sans-serif", outline: "none", boxSizing: "border-box" };
  const ta = { ...inp, minHeight: 80, resize: "vertical" };

  const salva = async () => {
    if (!form.motivoSopralluogo.trim()) { addToast("Inserisci il motivo del sopralluogo", "error"); return; }
    if (libero && !form.clienteId && !String(form.nomeCliente || "").trim()) { addToast("Scegli il cliente o scrivi il nome del potenziale cliente", "error"); return; }
    const rc = form.rcId ? rcElenco.find(r => r.id === form.rcId) : null;
    const dati = libero
      ? { ...form, projectId: "", projectName: "", codiceCommessa: "", clienteNome: String(form.nomeCliente || "").trim(),
          rcNumero: rc?.numero || (form.rcId ? form.rcNumero : "") || "", orgId, updatedAt: serverTimestamp(),
          ...(sopralluogo?.id ? {} : { creatoDa: { uid: currentUser?.uid || "", nome: currentUser?.displayName || "" } }) }
      : { ...form, projectId, projectName, codiceCommessa: codiceCommessa || "", clienteNome: clienteNome || form.nomeCliente,
          rcNumero: rc?.numero || (form.rcId ? form.rcNumero : "") || "", orgId, updatedAt: serverTimestamp() };
    if (sopralluogo?.id) {
      await updateDoc(doc(db, "sopralluoghi", sopralluogo.id), dati);
      addToast("Sopralluogo aggiornato ✅", "success");
    } else {
      const anno = new Date().getFullYear();
      const progressivo = await prossimoProgressivo(`${orgId}_sopralluoghi_${anno}`, { orgId, anno });
      const numero = `SOP-${anno}-${String(progressivo).padStart(4, "0")}`;
      await addDoc(collection(db, "sopralluoghi"), { ...dati, numero, createdAt: serverTimestamp() });
      addToast(`Sopralluogo ${numero} creato ✅`, "success");
    }
    bozza.salvato();
    onClose();
  };

  const creaTask = async (anomalia) => {
    if (!anomalia.descrizione.trim()) return;
    await addDoc(collection(db, "tasks"), {
      title: `[SOP] ${anomalia.descrizione}`,
      projectId, organizationId: orgId,
      status: "todo", priority: anomalia.gravita === "alta" ? "alta" : "normale",
      createdBy: currentUser?.uid, createdByName: currentUser?.displayName,
      dueDate: null, createdAt: serverTimestamp(),
      notaSorgente: "sopralluogo",
    });
    addToast("Task creato dalla anomalia ✅", "success");
    if (onTaskCreato) onTaskCreato();
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 680, maxHeight: "94vh", overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
            <FileText size={15} color="#2563eb"/> {sopralluogo ? "Modifica sopralluogo" : "Nuovo verbale di sopralluogo"}
          </h3>
          <button className="btn-icon" onClick={onClose}><X size={15}/></button>
        </div>

        {bozza.trovata && <AvvisoBozza testo={sopralluogo ? "Hai modifiche non salvate a questo sopralluogo" : "Hai un sopralluogo iniziato e non salvato"}
          salvataIl={bozza.trovata.salvataIl} onRiprendi={bozza.riprendi} onElimina={bozza.scarta} />}

        {/* Dati azienda */}
        {orgInfo && (
          <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, padding: "8px 12px", marginBottom: 12, display: "flex", alignItems: "center", gap: 10 }}>
            {orgInfo.logo ? <img src={orgInfo.logo} alt="Logo" style={{ height: 32, objectFit: "contain" }}/> : <div style={{ fontSize: 14, fontWeight: 800, color: "#2563eb" }}>{orgInfo.nome || orgInfo.name}</div>}
            <div style={{ fontSize: 11, color: "#1d4ed8" }}>
              <div style={{ fontWeight: 700 }}>{orgInfo.nome || orgInfo.name}</div>
              {orgInfo.indirizzo && <div>{orgInfo.indirizzo}</div>}
            </div>
          </div>
        )}

        {/* Commessa — oppure, senza commessa, il cliente o potenziale cliente (W416) */}
        {libero ? (
          <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "9px 12px", marginBottom: 12 }}>
            <div style={{ fontSize: 11.5, color: "#92400e", marginBottom: 8, lineHeight: 1.5 }}>
              <b>Senza commessa.</b> Quando la commessa nascerà, lo colleghi con «Collega a commessa»: da lì in poi sarà come fatto nella commessa.
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(220px, 100%), 1fr))", gap: 10 }}>
              <div>
                <label className="form-label">Cliente *</label>
                <select aria-label="Cliente" style={inp} value={form.clienteId || (form.potenziale || form.nomeCliente ? "__nuovo" : "")}
                  onChange={e => {
                    const v = e.target.value;
                    if (v === "__nuovo") setForm(f => ({ ...f, potenziale: true, clienteId: "", nomeCliente: f.clienteId ? "" : f.nomeCliente, sede: null, luogo: f.clienteId ? "" : f.luogo }));
                    else { const c = clienti.find(x => x.id === v); setForm(f => ({ ...f, potenziale: false, clienteId: v, nomeCliente: c?.ragioneSociale || "", sede: null, luogo: "" })); }
                  }}>
                  <option value="">— Scegli —</option>
                  {clienti.map(c => <option key={c.id} value={c.id}>{c.ragioneSociale}</option>)}
                  <option value="__nuovo">Potenziale cliente (scrivi il nome)</option>
                </select>
              </div>
              <div>
                <label className="form-label">Richiesta Commerciale</label>
                <select aria-label="Richiesta Commerciale" style={inp} value={form.rcId || ""}
                  onChange={e => { const r = rcElenco.find(x => x.id === e.target.value); setForm(f => ({ ...f, rcId: r?.id || "", rcNumero: r?.numero || "" })); }}>
                  <option value="">— Nessuna —</option>
                  {rcProposte.map(r => <option key={r.id} value={r.id}>{etichettaRC(r)}</option>)}
                </select>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, padding: "7px 12px", marginBottom: 12 }}>
            <span style={{ fontSize: 11, color: "#6b7280" }}>Commessa: </span>
            <span style={{ fontSize: 12, fontWeight: 700, color: "#7c3aed" }}>{codiceCommessa && `[${codiceCommessa}] `}{projectName}</span>
            {clienteNome && <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: 8 }}>— {clienteNome}</span>}
            {rcProposte.length > 0 && (
              <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                <span style={{ fontSize: 11, color: "#6b7280" }}>Richiesta Commerciale:</span>
                <select aria-label="Richiesta Commerciale" style={{ ...inp, width: "auto", minWidth: 200, flex: 1 }} value={form.rcId || ""}
                  onChange={e => { const r = rcElenco.find(x => x.id === e.target.value); setForm(f => ({ ...f, rcId: r?.id || "", rcNumero: r?.numero || "" })); }}>
                  <option value="">— Nessuna —</option>
                  {rcProposte.map(r => <option key={r.id} value={r.id}>{etichettaRC(r)}</option>)}
                </select>
              </div>
            )}
          </div>
        )}

        {/* Data, ora, luogo */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: 10, marginBottom: 12 }}>
          <div><label className="form-label">Data *</label><input type="date" style={inp} value={form.data} onChange={e => upd("data", e.target.value)}/></div>
          <div><label className="form-label">Ora</label><input type="time" style={inp} value={form.ora} onChange={e => upd("ora", e.target.value)}/></div>
          <div>
            {/* Luogo: dalle sedi del cliente (proposta quella della commessa) */}
            <SelettoreSede clienteId={commessa?.clienteId||form.clienteId||""} etichetta="Luogo / Indirizzo"
              value={form.sede || (form.luogo ? copiaLibera(form.luogo) : null)}
              proposta={!sopralluogo ? (commessa?.sede || null) : null}
              onChange={c=>setForm(f=>({...f, sede:c||null, luogo:descrizioneSede(c)}))}/>
          </div>
        </div>

        {/* Cliente */}
        <div style={{ marginBottom: 12 }}>
          <label className="form-label">{libero && !form.clienteId ? "Nome del potenziale cliente *" : "Nome cliente / Committente"}</label>
          <input aria-label="Nome cliente" style={inp} value={form.nomeCliente} disabled={libero && !!form.clienteId}
            onChange={e => upd("nomeCliente", e.target.value)} placeholder={libero ? "Azienda o persona non ancora cliente" : "Nome cliente"}/>
        </div>

        {/* Personale presente */}
        <div className="form-group">
          <label className="form-label">Personale aziendale presente</label>
          {form.personalePresente.map((p, i) => (
            <div key={i} style={{ display: "flex", gap: 6, marginBottom: 4 }}>
              <input style={{ ...inp, flex: 1 }} value={p}
                onChange={e => { const a = [...form.personalePresente]; a[i] = e.target.value; upd("personalePresente", a); }}
                placeholder="Nome e cognome"/>
              {form.personalePresente.length > 1 && (
                <button onClick={() => upd("personalePresente", form.personalePresente.filter((_, idx) => idx !== i))}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#fca5a5" }}><X size={12}/></button>
              )}
            </div>
          ))}
          <button onClick={() => upd("personalePresente", [...form.personalePresente, ""])}
            style={{ fontSize: 11, color: "#7c3aed", background: "none", border: "none", cursor: "pointer" }}>+ Aggiungi persona</button>
        </div>

        {/* Altre persone presenti */}
        <div className="form-group">
          <label className="form-label">Altre persone presenti (cliente, DL, ecc.)</label>
          <input style={inp} value={form.altrePersone} onChange={e => upd("altrePersone", e.target.value)} placeholder="Es. Ing. Rossi (DL), Sig. Bianchi (referente cliente)"/>
        </div>

        {/* Motivo */}
        <div className="form-group">
          <label className="form-label">Motivo del sopralluogo *</label>
          <textarea style={{ ...ta, minHeight: 60 }} value={form.motivoSopralluogo} onChange={e => upd("motivoSopralluogo", e.target.value)} placeholder="Motivo per cui è stato effettuato il sopralluogo..."/>
        </div>

        {/* Situazione rilevata */}
        <div className="form-group">
          <label className="form-label">Situazione rilevata</label>
          <textarea style={ta} value={form.situazioneRilevata} onChange={e => upd("situazioneRilevata", e.target.value)} placeholder="Descrizione dettagliata di ciò che è stato rilevato durante il sopralluogo..."/>
        </div>

        {/* Anomalie */}
        <div className="form-group">
          <label className="form-label">⚠️ Anomalie / Criticità riscontrate</label>
          <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 8 }}>
            {libero ? "Dopo il collegamento a una commessa, ogni anomalia potrà diventare un'attività con un clic." : "Ogni anomalia può essere trasformata in un task con un click."}
          </div>
          {form.anomalie.map((an, i) => (
            <div key={i} style={{ background: "#fef9f0", border: "1px solid #fde68a", borderRadius: 8, padding: "8px 10px", marginBottom: 8, display: "flex", gap: 8, alignItems: "flex-start" }}>
              <div style={{ flex: 1 }}>
                <input style={{ ...inp, marginBottom: 4 }} value={an.descrizione}
                  onChange={e => { const a = [...form.anomalie]; a[i] = { ...a[i], descrizione: e.target.value }; upd("anomalie", a); }}
                  placeholder="Descrivi l'anomalia o criticità..."/>
                <select value={an.gravita}
                  onChange={e => { const a = [...form.anomalie]; a[i] = { ...a[i], gravita: e.target.value }; upd("anomalie", a); }}
                  style={{ ...inp, width: "auto", fontSize: 11 }}>
                  <option value="bassa">Gravità bassa</option>
                  <option value="media">Gravità media</option>
                  <option value="alta">Gravità alta ⚠️</option>
                </select>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {!libero && sopralluogo?.id && an.descrizione.trim() && (
                  <button onClick={() => creaTask(an)} title="Crea task da questa anomalia"
                    style={{ padding: "3px 8px", border: "1px solid #2563eb", borderRadius: 6, background: "#dbeafe", color: "#1d4ed8", cursor: "pointer", fontSize: 10, fontWeight: 700 }}>
                    <CheckSquare size={11}/> Task
                  </button>
                )}
                {form.anomalie.length > 1 && (
                  <button onClick={() => upd("anomalie", form.anomalie.filter((_, idx) => idx !== i))}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "#fca5a5" }}><X size={12}/></button>
                )}
              </div>
            </div>
          ))}
          <button onClick={() => upd("anomalie", [...form.anomalie, { descrizione: "", gravita: "media" }])}
            style={{ fontSize: 11, color: "#2563eb", background: "none", border: "none", cursor: "pointer" }}>+ Aggiungi anomalia</button>
        </div>

        {/* Prescrizioni */}
        <div className="form-group">
          <label className="form-label">Prescrizioni e raccomandazioni</label>
          <textarea style={{ ...ta, minHeight: 70 }} value={form.prescrizioni} onChange={e => upd("prescrizioni", e.target.value)} placeholder="Prescrizioni tecniche, raccomandazioni, indicazioni specifiche..."/>
        </div>

        {/* Attività da eseguire */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
          <div>
            <label className="form-label">Attività da eseguire</label>
            <textarea style={{ ...ta, minHeight: 70 }} value={form.attivitaDaEseguire} onChange={e => upd("attivitaDaEseguire", e.target.value)} placeholder="Attività pianificate a seguito del sopralluogo..."/>
          </div>
          <div>
            <label className="form-label">Materiali / Mezzi necessari</label>
            <textarea style={{ ...ta, minHeight: 70 }} value={form.materialiNecessari} onChange={e => upd("materialiNecessari", e.target.value)} placeholder="Materiali e attrezzature necessarie..."/>
          </div>
        </div>

        {/* Foto */}
        <div className="form-group">
          <label className="form-label">📷 Foto</label>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", background: "#f3f4f6", border: "1px solid #e5e7eb", borderRadius: 7, cursor: "pointer", fontSize: 11 }}>
            📁 Aggiungi foto
            <input type="file" accept="image/*" multiple style={{ display: "none" }}
              onChange={async e => {
                const files = Array.from(e.target.files);
                e.target.value = "";
                try {
                  const compresse = await comprimiImmagini(files);
                  upd("foto", [...(form.foto || []), ...compresse]);
                } catch(err) {
                  addToast(err.message||"Errore nel caricamento di una foto","error");
                }
              }}/>
          </label>
          {(form.foto || []).length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
              {(form.foto || []).map((f, i) => (
                <div key={i} style={{ position: "relative" }}>
                  <img src={f.data} alt={f.nome} style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 6, border: "1px solid #e5e7eb" }}/>
                  <button onClick={() => upd("foto", (form.foto || []).filter((_, idx) => idx !== i))}
                    style={{ position: "absolute", top: -6, right: -6, width: 18, height: 18, borderRadius: "50%", background: "#dc2626", color: "#fff", border: "none", cursor: "pointer", fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Note */}
        <div className="form-group">
          <label className="form-label">Note</label>
          <textarea style={{ ...ta, minHeight: 60 }} value={form.note} onChange={e => upd("note", e.target.value)} placeholder="Note aggiuntive..."/>
        </div>

        {/* Firma */}
        <div className="form-group">
          <label className="form-label">Firma</label>
          {form.firma ? (
            <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: 10, background: "#f9fafb" }}>
              <FirmaImmagine src={form.firma} alt="Firma" style={{ maxWidth: 280, maxHeight: 100, display: "block" }}/>
              <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>Firmato da: <strong>{form.nomeFirmatario || "—"}</strong></div>
              <button onClick={() => { upd("firma", null); upd("nomeFirmatario", ""); }}
                style={{ fontSize: 11, color: "#dc2626", background: "none", border: "none", cursor: "pointer", marginTop: 4 }}>Rimuovi firma</button>
            </div>
          ) : showFirma ? (
            <div>
              <div className="form-group">
                <label className="form-label">Nome del firmatario</label>
                <input style={inp} value={form.nomeFirmatario} onChange={e => upd("nomeFirmatario", e.target.value)} placeholder="Nome e cognome"/>
              </div>
              <FirmaCanvas label="Firma" onSave={data => { upd("firma", data); setShowFirma(false); }} onCancel={() => setShowFirma(false)}/>
            </div>
          ) : (
            <button onClick={() => setShowFirma(true)}
              style={{ padding: "6px 14px", border: "1px solid #2563eb", borderRadius: 7, background: "#dbeafe", color: "#2563eb", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
              ✍️ Raccogli firma
            </button>
          )}
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
          <button className="btn btn-secondary" onClick={onClose}>Annulla</button>
          <button className="btn btn-primary" onClick={salva}>Salva sopralluogo</button>
        </div>
      </div>
    </div>
  );
}

// Card sopralluogo (anche nella pagina «Sopralluoghi senza commessa»: onCollega)
export function SopralluogoCard({ s, orgInfo, projectName, onEdit, onDelete, onCollega }) {
  const [aperto, setAperto] = useState(false);
  const stato = STATI_SOP[s.stato] || STATI_SOP.bozza;

  const { addToast } = useToast();
  const stampaPDF = () => { if (!stampaSopralluogo(s, orgInfo, projectName)) addToast("Il browser ha bloccato la finestra del verbale: consenti le finestre pop-up per Nexiora", "error"); };

  const anomalieConDescrizione = (s.anomalie || []).filter(a => a.descrizione);

  return (
    <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, marginBottom: 10, overflow: "hidden" }}>
      <div style={{ padding: "10px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }} onClick={() => setAperto(!aperto)}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2, flexWrap: "wrap" }}>
            <FileText size={13} color="#2563eb"/>
            {s.numero && <span style={{ fontSize: 13, fontWeight: 800, color: "#1d4ed8", fontFamily: "monospace" }}>{s.numero}</span>}
            <span style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
              {s.data ? new Date(s.data+"T00:00:00").toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "long", year: "numeric" }) : "—"}
            </span>
            <span style={{ fontSize: 10, padding: "1px 7px", borderRadius: 10, background: stato.bg, color: stato.col, fontWeight: 700 }}>{stato.label}</span>
            {anomalieConDescrizione.length > 0 && (
              <span style={{ fontSize: 10, padding: "1px 7px", borderRadius: 10, background: "#fef3c7", color: "#92400e", fontWeight: 700 }}>
                ⚠️ {anomalieConDescrizione.length} anomali{anomalieConDescrizione.length === 1 ? "a" : "e"}
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: "#9ca3af" }}>
            {senzaCommessa(s) && <b style={{ color: "#92400e" }}>{clienteDelSopralluogo(s) || "Cliente non indicato"}{s.clienteId ? "" : " (potenziale)"} · </b>}
            📍 {s.luogo || "—"} · {(s.personalePresente || []).join(", ")}
            {s.rcNumero && <span style={{ marginLeft: 6, fontSize: 10, padding: "1px 7px", borderRadius: 10, background: "#ede9fe", color: "#5b21b6", fontWeight: 700 }}>da {s.rcNumero}</span>}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
          {onCollega && senzaCommessa(s) && (
            <button onClick={e => { e.stopPropagation(); onCollega(); }}
              style={{ fontSize: 11, padding: "3px 9px", border: "1px solid #7c3aed", borderRadius: 6, background: "#f5f3ff", cursor: "pointer", color: "#6d28d9", fontWeight: 700 }}>
              🔗 Collega a commessa
            </button>
          )}
          <button onClick={e => { e.stopPropagation(); stampaPDF(); }}
            style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 11, padding: "3px 8px", border: "1px solid #e5e7eb", borderRadius: 6, background: "#fff", cursor: "pointer" }}>
            <Printer size={11}/> PDF
          </button>
          <button onClick={e => { e.stopPropagation(); onEdit(); }}
            style={{ fontSize: 11, padding: "3px 8px", border: "1px solid #e5e7eb", borderRadius: 6, background: "#fff", cursor: "pointer", color: "#6b7280" }}>
            Modifica
          </button>
          <button onClick={e => { e.stopPropagation(); onDelete(); }}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#fca5a5" }}>
            <X size={13}/>
          </button>
          {aperto ? <ChevronDown size={14} color="#9ca3af"/> : <ChevronRight size={14} color="#9ca3af"/>}
        </div>
      </div>
      {aperto && (
        <div style={{ borderTop: "1px solid #f3f4f6", padding: "12px 16px" }}>
          {s.motivoSopralluogo && <div style={{ marginBottom: 8 }}><div style={{ fontSize: 11, fontWeight: 700, color: "#374151", marginBottom: 3 }}>📋 Motivo</div><div style={{ fontSize: 12, color: "#6b7280" }}>{s.motivoSopralluogo}</div></div>}
          {s.situazioneRilevata && <div style={{ marginBottom: 8 }}><div style={{ fontSize: 11, fontWeight: 700, color: "#374151", marginBottom: 3 }}>🔍 Situazione rilevata</div><div style={{ fontSize: 12, color: "#6b7280", whiteSpace: "pre-wrap" }}>{s.situazioneRilevata}</div></div>}
          {anomalieConDescrizione.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#d97706", marginBottom: 6 }}>⚠️ Anomalie riscontrate</div>
              {anomalieConDescrizione.map((a, i) => (
                <div key={i} style={{ fontSize: 12, color: "#6b7280", marginBottom: 3, display: "flex", gap: 6, alignItems: "center" }}>
                  <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 8, background: a.gravita==="alta"?"#fee2e2":a.gravita==="media"?"#fef3c7":"#d1fae5", color: a.gravita==="alta"?"#991b1b":a.gravita==="media"?"#92400e":"#065f46", fontWeight: 700 }}>{a.gravita}</span>
                  {a.descrizione}
                </div>
              ))}
            </div>
          )}
          {s.prescrizioni && <div style={{ marginBottom: 8 }}><div style={{ fontSize: 11, fontWeight: 700, color: "#7c3aed", marginBottom: 3 }}>📌 Prescrizioni</div><div style={{ fontSize: 12, color: "#6b7280", whiteSpace: "pre-wrap" }}>{s.prescrizioni}</div></div>}
          {(s.foto||[]).length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#374151", marginBottom: 6 }}>📷 Foto</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {(s.foto||[]).map((f, i) => (
                  <img key={i} src={f.data} alt={f.nome||"foto"} style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 6, border: "1px solid #e5e7eb", cursor: "pointer" }}
                    onClick={() => window.open(f.data, "_blank")}/>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Nella scheda Sopralluogo della commessa: i sopralluoghi fatti prima della commessa per lo
// stesso cliente (o nati da una sua Richiesta Commerciale), da collegare con un clic (W416).
function DaCollegare({ orgId, projectId, commessa, onCollegato }) {
  const { currentUser } = useAuth();
  const { addToast } = useToast();
  const [liberi, setLiberi] = useState([]);
  const [rcCommessa, setRcCommessa] = useState([]);
  const [padre, setPadre] = useState(null);
  const [lavoro, setLavoro] = useState("");
  useEffect(() => {
    if (!orgId || !projectId) return undefined;
    const u = [
      onSnapshot(query(collection(db, "sopralluoghi"), where("orgId", "==", orgId), where("projectId", "==", "")),
        s => setLiberi(s.docs.map(d => ({ id: d.id, ...d.data() }))), () => setLiberi([])),
      onSnapshot(query(collection(db, "richiesteCommerciali"), where("orgId", "==", orgId), where("projectId", "==", projectId)),
        s => setRcCommessa(s.docs.map(d => ({ id: d.id }))), () => setRcCommessa([])),
    ];
    return () => u.forEach(f => f());
  }, [orgId, projectId]);
  useEffect(() => {
    if (!commessa?.parentId) { setPadre(null); return; }
    getDoc(doc(db, "projects", commessa.parentId)).then(s => setPadre(s.exists() ? { id: s.id, ...s.data() } : null)).catch(() => setPadre(null));
  }, [commessa?.parentId]);
  const progetto = commessa ? { ...commessa, id: projectId } : null;
  const elenco = progetto ? daCollegareAllaCommessa(liberi, progetto, rcCommessa, padre ? { [padre.id]: padre } : {}) : [];
  if (!elenco.length) return null;
  const collega = async (s) => {
    setLavoro(s.id);
    try { await collegaSopralluogo(s, progetto, currentUser || {}, padre ? { [padre.id]: padre } : {}); addToast(`${s.numero || "Sopralluogo"} collegato a questa commessa ✅`, "success"); onCollegato?.(); }
    catch { addToast("Collegamento non riuscito: riprova", "error"); }
    finally { setLavoro(""); }
  };
  return (
    <div role="region" aria-label="Sopralluoghi da collegare" style={{ background: "#f5f3ff", border: "1px solid #ddd6fe", borderLeft: "4px solid #7c3aed", borderRadius: 9, padding: "10px 14px", marginBottom: 14 }}>
      <div style={{ fontSize: 12.5, fontWeight: 800, color: "#5b21b6", marginBottom: 6 }}>
        🔗 {elenco.length === 1 ? "C'è un sopralluogo fatto prima della commessa" : `Ci sono ${elenco.length} sopralluoghi fatti prima della commessa`} per questo cliente
      </div>
      {elenco.map(s => (
        <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "5px 0", borderTop: "1px dashed #ddd6fe" }}>
          <span style={{ fontSize: 12, color: "#374151", flex: 1, minWidth: 200 }}>
            <b style={{ fontFamily: "monospace", color: "#1d4ed8" }}>{s.numero}</b> · {s.data ? new Date(s.data + "T00:00:00").toLocaleDateString("it-IT") : "—"} · {s.motivoSopralluogo || "—"}{s.rcNumero ? ` · da ${s.rcNumero}` : ""}
          </span>
          <button type="button" disabled={!!lavoro} onClick={() => collega(s)}
            style={{ fontSize: 11.5, fontWeight: 700, padding: "4px 12px", borderRadius: 7, border: "none", background: "#7c3aed", color: "#fff", cursor: lavoro ? "wait" : "pointer" }}>
            {lavoro === s.id ? "Collego…" : "Collega a questa commessa"}
          </button>
        </div>
      ))}
    </div>
  );
}

// Componente principale
export default function SopralluogoTab({ projectId, projectName, codiceCommessa, clienteNome, orgId }) {
  // Dati della commessa per proporre il luogo (sede dei lavori)
  const [commessa, setCommessa] = useState(null);
  useEffect(() => { if (projectId) getDoc(doc(db, "projects", projectId)).then(s => setCommessa(s.exists() ? s.data() : null)).catch(() => {}); }, [projectId]);
  const { addToast } = useToast();
  const [sopralluoghi, setSopralluoghi] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editSop, setEditSop] = useState(null);
  const [orgInfo, setOrgInfo] = useState(null);
  const { currentUser } = useAuth();
  const [riprendiBozza, setRiprendiBozza] = useState(false);
  const canSee = useCanSee();
  const puoCollegare = canSee(PERMESSO_SENZA_COMMESSA);

  useEffect(() => {
    if (!projectId || !orgId) return;
    return onSnapshot(
      query(collection(db, "sopralluoghi"), where("projectId", "==", projectId), where("orgId", "==", orgId)),
      snap => setSopralluoghi(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => b.data?.localeCompare(a.data || "") || 0))
    );
  }, [projectId, orgId]);

  useEffect(() => {
    if (!orgId) return;
    getDoc(doc(db, "organizations", orgId)).then(snap => { if (snap.exists()) setOrgInfo(snap.data()); });
  }, [orgId]);

  const elimina = async (id) => {
    if (!await confirm("Eliminare questo sopralluogo?", { type: "danger" })) return;
    await deleteDoc(doc(db, "sopralluoghi", id));
    addToast("Eliminato", "info");
  };

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div>
          <h3 style={{ fontSize: 15, fontWeight: 700, display: "flex", alignItems: "center", gap: 6, margin: 0 }}>
            <AlertTriangle size={15} color="#2563eb"/> Verbali di sopralluogo
          </h3>
          <p style={{ fontSize: 11, color: "#9ca3af", margin: "2px 0 0" }}>{sopralluoghi.length} verbale/i</p>
        </div>
        <button onClick={() => { setEditSop(null); setRiprendiBozza(false); setShowModal(true); }} className="btn btn-primary"
          style={{ display: "flex", alignItems: "center", gap: 5, background: "#2563eb", borderColor: "#2563eb" }}>
          <Plus size={13}/> Nuovo sopralluogo
        </button>
      </div>

      {puoCollegare && <DaCollegare orgId={orgId} projectId={projectId} commessa={commessa} />}

      {/* sopralluoghi iniziati e non salvati su questo dispositivo */}
      {!showModal && <ElencoBozze prefisso={prefissoBozze(currentUser?.uid, "sopralluogo", projectId)} documenti={sopralluoghi}
        testoDi={(b, s) => s ? `Modifiche non salvate al sopralluogo ${s.numero || ""}` : `Sopralluogo non salvato${delGiorno(b.info?.data)}`}
        onRiprendi={s => { setEditSop(s); setRiprendiBozza(true); setShowModal(true); }} />}

      {sopralluoghi.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60, color: "#9ca3af" }}>
          <AlertTriangle size={48} style={{ opacity: 0.15, marginBottom: 16 }}/>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Nessun sopralluogo</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>Crea il primo verbale di sopralluogo per questa commessa</div>
        </div>
      ) : sopralluoghi.map(s => (
        <SopralluogoCard key={s.id} s={s} orgInfo={orgInfo} projectName={projectName}
          onEdit={() => { setEditSop(s); setRiprendiBozza(false); setShowModal(true); }}
          onDelete={() => elimina(s.id)}/>
      ))}

      {showModal && (
        <SopralluogoModal
          commessa={commessa}
          sopralluogo={editSop}
          projectId={projectId} projectName={projectName}
          codiceCommessa={codiceCommessa} clienteNome={clienteNome}
          orgId={orgId} orgInfo={orgInfo}
          riprendiBozza={riprendiBozza}
          onClose={() => { setShowModal(false); setEditSop(null); setRiprendiBozza(false); }}
          onTaskCreato={() => addToast("Task creato e collegato alla commessa ✅", "success")}/>
      )}
    </div>
  );
}
